import * as DOMUtils from "history/DOMUtils";
import { createLogic } from "redux-logic";
import Debug from "../../../../../common-deprecated/Debug";
import { NavOrigin, NavStep } from "../../../shared-logic/utils/Navigation";
import type { SetStepType, SetNavigationOriginType } from "../actions/NavigationActions";
import { NAVIGATION_SET_STEP, NAVIGATION_SET_ORIGIN, setNavigationOrigin } from "../actions/NavigationActions";
import type { BuildAndBuyLogicType } from "../store";
import * as History from "../../../../../common-deprecated/History";
import { showModal } from "../../../../../common-deprecated/redux/actions/ModalActions";
import { isMassValidationOk } from "../../utils/typedUtils";
import { startConfigSave } from "../actions/SaveActions";

import { MODAL_CONTENT } from "../../../../../common-deprecated/utils/modalConstants";
import { getSessionStorageParams, setNavigationOriginToStorage } from "../../utils/sessionStorage";
import { getBnbLabel } from "../../utils/labels";
import { startExternalPriceValueFetch } from "../actions/ExternalPriceActions";
import { calculateTotalPrice } from "../../../../shared-logic/utils/priceUtils";
import { getDefaultPriceObject } from "../../../../../common-deprecated/constants";
import { showBnbMonthlyRates } from "../../utils/settings/bnbFinanceSettingUtils";
import type { BasketType } from "../../../../shared-logic/types/BasketType";

const setStepLogic = (createLogic as BuildAndBuyLogicType<SetStepType>)({
    type: NAVIGATION_SET_STEP,

    validate({ getState, action }, allow, reject) {
        const { navigation, commonSettings, massManagement, bnbSettings } = getState();

        // Check if the mass validates, if so allow, if not throw an error message
        if (action.step === NavStep.Buy && !isMassValidationOk(massManagement)) {
            return reject(
                showModal({
                    type: MODAL_CONTENT,
                    content: getBnbLabel({ bnbSettings, commonSettings }, "massManagementPopupNotification"),
                }),
            );
        }

        if (bnbSettings.enableBuildSPA) {
            // no need to check the current step as this setting is only enabled from customize onwards
            return allow(action);
        }

        /**
         * setStep call is only permitted to be called in the following circumstances:
         * Going to buy/reserve/lease from customize/services/summary
         * Going to customize/services/summary from buy/reserve/lease
         * Going to customize/services from summary
         */
        const goingBackFromSummary =
            [NavStep.Customize, NavStep.Services].includes(action.step) && navigation.step === NavStep.Summary;

        const goingToCheckout =
            (action.step === NavStep.Buy || action.step === NavStep.Reserve || action.step === NavStep.Lease) &&
            [NavStep.Customize, NavStep.Services, NavStep.Summary].includes(navigation.step);

        const goingBackFromCheckout =
            [NavStep.Customize, NavStep.Services, NavStep.Summary].includes(action.step) &&
            [NavStep.Buy, NavStep.Reserve, NavStep.Lease].includes(navigation.step);

        if (DOMUtils.canUseDOM && (goingBackFromSummary || goingToCheckout || goingBackFromCheckout)) {
            allow(action);
        } else {
            Debug.error(
                "setStep is only allowed between buy/reserve step <-> customize-summary and summary step <-> customize-services.",
            );
            (reject as () => void)();
        }
    },

    process({ action, getState }, dispatch, done) {
        const { save, bnbSettings, basket, car, externalPrice, navigation, commonSettings } = getState();

        // Logic for the bug in OR-2620. Only works in Build V1
        if (action.step === NavStep.Summary && !save.saved && !save.saving) {
            dispatch(startConfigSave());
        }
        // Logic for OR-8265
        if (action.step === NavStep.Summary && externalPrice.enabled) {
            const calculatedTotal = calculateTotalPrice(
                getDefaultPriceObject(),
                basket,
                car,
                showBnbMonthlyRates(commonSettings, bnbSettings, car.model.internalCode, navigation.step, car),
            ) as BasketType["total"];
            dispatch(startExternalPriceValueFetch(calculatedTotal, false));
        }

        const goingToCheckout =
            action.step === NavStep.Buy || action.step === NavStep.Reserve || action.step === NavStep.Lease;

        const goingBackFromSummary = [NavStep.Customize, NavStep.Services].includes(action.step);

        if (History.historyIsEnabled() && ((bnbSettings.enableBuildSPA && !goingToCheckout) || goingBackFromSummary)) {
            const path = `${navigation.step}/${car.model.id}/${car.id}`; //only supports customize onwards, for now
            History.replaceQueryParams({ path });
        }

        // If you require certain actions to occur post navigation when triggered in a specific way
        // Add a origin in navOrigins
        // Capture the sessionStorage change in your component
        // Execute your specific logic if origin changes to your specific origin
        if (action.origin) {
            dispatch(setNavigationOrigin(action.origin));
        }
        done();
    },
});

const setStepNavigationOrigin = (createLogic as BuildAndBuyLogicType<SetNavigationOriginType>)({
    type: NAVIGATION_SET_ORIGIN,
    process({ action, getState }, dispatch, done) {
        const { commonSettings } = getState();
        const sessionStorageParams = getSessionStorageParams(commonSettings);

        // Sets the Origin to the passed value
        setNavigationOriginToStorage(sessionStorageParams, action.origin || NavOrigin.Unknown);

        done();
    },
});

export default [setStepLogic, setStepNavigationOrigin];
